<script lang="ts">
import { Users, Handshake, Ellipsis } from '@lucide/vue';

export default {
  name: 'TowerCard',
  components: {
    Users,
    Handshake,
    Ellipsis
  },
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { TowerCardData, TowerType } from '@/views/tower_dashboard/types/tower-dashboard';
import { DateTime } from 'luxon';
import { AppState } from '@/store';
import { useStore } from 'vuex';
import { IsUserABroker, IsUserAnAdmin } from '@/utilities/AuthUtils';

const props = defineProps<{
  cardData: TowerCardData,
  optionsOpen?: boolean,
  highlightTowerId?: string,
  highlightTowerBorderColor?: string,
}>();

const emit = defineEmits<{
  (e: 'click', towerId: string): void;
  (e: 'edit', towerId: string): void;
  (e: 'duplicate', towerId: string): void;
  (e: 'toggle-publish', towerId: string, published: boolean): void;
  (e: 'delete', towerCard: TowerCardData): void;
  (e: 'update:optionsOpen', optionsOpen: boolean): void;
  (e: 'convert:new-coverage', towerId: string): void;
  (e: 'convert:current-coverage', towerId: string, accountId: number): void;
}>();

const store = useStore<AppState>();

interface TowerTypeChip {
  bgColor: string;
  textColor: string;
  text: string;
}

const towerChips: Record<TowerType, TowerTypeChip> = {
  [TowerType.BoundCoverage]: { bgColor: 'bg-blue-200', textColor: 'text-blue-900', text: 'Bound Coverage' },
  [TowerType.Renewal]: { bgColor: 'bg-red-200', textColor: 'text-red-900', text: 'Renewal' },
  [TowerType.NewCoverage]: { bgColor: 'bg-orange-200', textColor: 'text-orange-900', text: 'New Coverage' },
  [TowerType.Scratch]: { bgColor: 'bg-emerald-200', textColor: 'text-emerald-900', text: 'Scratch' },
  [TowerType.MultiTower]: { bgColor: 'bg-gray-200', textColor: 'text-gray-900', text: 'MultiTower' },
};

const showOptions = ref(false);
const optionsMenu = ref<HTMLElement>();
const showContributors = ref<boolean>(false);


// ---------
// computed
// ---------
const versionLabel = computed(() => {
  if (props.cardData.versionLabel) {
    if (props.cardData.versionLabel.length > 60) {
      return props.cardData.versionLabel.substring(0, 60).trim() + '...';
    }
    return props.cardData.versionLabel;
  }
  return 'Initial Version';
});

const programName = computed(() => {
  let programName = '';
  if (props.cardData.program) {
    const program = props.cardData.program;
    
    if (program.type === 'Manual') {
      programName = `Manual ${program.year} ${program.name}`;
    } else {
      programName = program.name;
    }

    if (programName) {
      if (programName.length > 50) {
        return programName.substring(0, 50).trim() + '...';
      }
    }
    
    return programName;
  }

  return '';
});

const towerName = computed(() => {
  if (props.cardData.towerName) {
    if (props.cardData.towerName.length > 50) {
      return props.cardData.towerName.substring(0, 50).trim() + '...';
    }
    return props.cardData.towerName;
  }
  return '';
});

const accountName = computed(() => {
  if (props.cardData.accountName) {
    if (props.cardData.accountName.length > 50) {
      return props.cardData.accountName.substring(0, 50).trim() + '...';
    }

    return props.cardData.accountName;
  }
  return '';
});

const userEmail = computed(() => {
  if (props.cardData.user.userEmail) {
    if (props.cardData.user.userEmail.length > 50) {
      return props.cardData.user.userEmail.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.userEmail;
  }
  return '';
});

const userName = computed(() => {
  if (props.cardData.user.name) {
    if (props.cardData.user.name.length > 50) {
      return props.cardData.user.name.substring(0, 50).trim() + '...';
    }

    return props.cardData.user.name;
  }
  return '';
});

const canDelete = computed(() => {
  if (props.cardData.published) {
    return false;
  }

  if (IsUserAnAdmin(store.state.user)) {
    return true;
  }

  if (IsUserABroker(store.state.user) && store.state.user?.user.id === props.cardData.user.id) {
    return true;
  }

  return false;
});

const canConvertToNewCoverageTower = computed(() => {
  return props.cardData.towerTypeId === TowerType.Scratch
});

const canConvertToBoundCoverageTower = computed(() => {
  return props.cardData.towerTypeId === TowerType.NewCoverage || props.cardData.towerTypeId === TowerType.Renewal
});

const updatedOn = computed(() => {
  if (props.cardData.updatedOn) {
    let date = DateTime.fromISO(props.cardData.updatedOn);

    if (date.startOf('day').equals(DateTime.now().startOf('day'))) {
      return date.toLocaleString(DateTime.TIME_SIMPLE);
    }

    return date.toLocaleString(DateTime.DATE_MED) + ' - ' + date.toLocaleString(DateTime.TIME_SIMPLE);
  }
  return '';
});

const highlightCard = computed(() => {
  return props.highlightTowerId === props.cardData.towerId && props.highlightTowerBorderColor !== undefined;
});

// ---------
// methods
// ---------
function handleEditTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('edit', props.cardData.towerId);
}

function handleDuplicateTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('duplicate', props.cardData.towerId);
}

function handleConvertToNewCoverageTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('convert:new-coverage', props.cardData.towerId);
}

function handleConvertToBoundCoverageTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  console.log('convert to bound coverage', props.cardData);
  emit('convert:current-coverage', props.cardData.towerId, props.cardData.accountId ?? 0);
}

function handleDeleteTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('delete', props.cardData);
}

function handlePublishTower(e: Event) {
  e.stopPropagation();
  optionsMenu.value?.blur();
  emit('toggle-publish', props.cardData.towerId, !props.cardData.published);
}

function toggleOptionsMenu(e: Event) {
  e.stopPropagation();
  showOptions.value = !showOptions.value;
  emit('update:optionsOpen', showOptions.value)

  // set focus on open for blur to work
  if (showOptions.value) {
    setTimeout(() => {
      optionsMenu.value?.focus();
    }, 50);
  }
}

function handleOptionsBlur(e: FocusEvent) {
  if (showOptions.value) {
    emit('update:optionsOpen', false);
    showOptions.value = false;
  }
}
</script>

<template>
  <div class="group flex group
         pointer-events-auto cursor-pointer relative">
    <div
      @click="$emit('click', cardData.towerId)"
      class="flex flex-col justify-between
        w-80 h-56
        rounded-md
        pointer-events-auto cursor-pointer
        px-2 py-2
        shadow-md border border-gray-300 border-solid
        bg-white
        transition-all duration-150
        group-hover:shadow-none group-hover:border-blue-200 group-hover:bg-blue-50 group
      text-[#133044] relative" :class="highlightCard ? `border-${highlightTowerBorderColor}` : ''">
      
      <div class="flex justify-between">
        <div class="w-full flex overflow-hidden px-2">
          <p :title="cardData.versionLabel" class="text-base font-medium grow break-words">
            {{ versionLabel }}
          </p>
        </div>

        <div ref="optionsMenu" tabindex="0" @blur="handleOptionsBlur" @click="toggleOptionsMenu"
             class="bg-white flex rounded-md overflow-hidden place-content-center cursor-pointer
              hover:bg-gray-100 h-8 w-10
              z-10">
          
          <Ellipsis class="text-gray-400 m-auto z-10" />

          <div v-if="showOptions" class="absolute right-2 top-11">
            <ul class="menu bg-white rounded-lg border border-solid border-gray-200 shadow-xs">
              <li>
                <a @click="handleEditTower" class="text-black">Rename</a>
                <a @click="handleDuplicateTower" class="text-black">Duplicate</a>
                <a v-if="canConvertToNewCoverageTower" @click="handleConvertToNewCoverageTower" class="text-black">Associate To Client</a>
                <a v-if="canConvertToBoundCoverageTower" @click="handleConvertToBoundCoverageTower" class="text-black">Associate To Program</a>
                <a @click="handlePublishTower" class="text-black">{{ cardData.published ? 'Unpublish' : 'Publish to Clients'}}</a>
                <a v-if="canDelete" @click="handleDeleteTower" class="text-primary">Delete</a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div
        class="flex mb-auto rounded-lg bg h-6 w-fit px-3 justify-center align-center"
        :class="towerChips[cardData.towerTypeId as TowerType].bgColor + ' ' + towerChips[cardData.towerTypeId as TowerType].textColor">
           <span class="my-auto">{{ towerChips[cardData.towerTypeId as TowerType].text
             }}</span>
      </div>

      <div class="ml-2 my-auto">
        <p class="  text-sm text-gray-600">
          {{ programName && programName !== '' ? programName : towerName }}</p>
        <p class="text-xs  font-bold">{{ accountName }}</p>
      </div>

      <div class="flex flex-row w-full gap-2 mt-auto text-xs text-end justify-between px-1">
        <div v-if="cardData.published" class="flex justify-end tooltip tooltip-top z-20" data-tip="Published to Clients">
          <Users class="text-green-500 self-end" :size="20"/>
        </div>
        
        <div v-if="props.cardData.contributors && props.cardData.contributors.length > 0" class="relative flex justify-end">
          <Handshake
            :size="20"
            class="text-gray-400 self-end"
            @mouseover="showContributors = true"
            @mouseleave="showContributors = false"
          />
          <div v-show="showContributors"
            class="absolute text-start whitespace-nowrap border border-gray-300 bg-white
              shadow-md px-4 py-2 rounded-md left-0 top-8 mt-1 z-50">
            <p class="text-sm font-bold mb-2">Contributors</p>
            <ul class="list-none m-0 p-0">
              <li v-for="(contributor, index) in props.cardData.contributors.split(',')" :key="index" class="text-sm">
                {{ contributor }}
              </li>
            </ul>
          </div>
        </div>

        <div class="flex flex-col mr-0 ml-auto">
          <p class="mb-0 mt-auto break-all">{{ userName !== '' ? userName : userEmail }}</p>
          <p class="mb-0 font text-gray-500">Updated {{ updatedOn }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
</style>
